import React, { type KeyboardEvent } from 'react'
import { Col, Row } from 'react-bootstrap'
import classNames from 'classnames'
import { type CustomIntegrationAuthType } from '../../../models/CustomIntegration'
import { AUTH_TYPE_OPTIONS } from '../form/authTypeOptions'
import styles from './AuthTypeSelector.module.scss'

type AuthTypeSelectorLayout = 'vertical' | 'horizontal'

interface AuthTypeSelectorProps {
  value: CustomIntegrationAuthType | null
  onChange?: (authType: CustomIntegrationAuthType) => void
  readOnly?: boolean
  layout?: AuthTypeSelectorLayout
  showHint?: boolean
}

export const AuthTypeSelector: React.FC<AuthTypeSelectorProps> = ({ value, onChange, readOnly = false, layout = 'vertical', showHint = true }) => {
  const vertical = layout === 'vertical'

  const handleSelect = (authType: CustomIntegrationAuthType) => {
    if (!readOnly) {
      onChange?.(authType)
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>, authType: CustomIntegrationAuthType) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleSelect(authType)
    }
  }

  return (
    <div className='d-flex flex-column gap-3'>
      <Row className='g-4' role='radiogroup' aria-label='Authorization type'>
        {AUTH_TYPE_OPTIONS.map((option) => {
          const selected = option.type === value
          const muted = readOnly && !selected
          return (
            <Col key={option.type} xs={12} md={6} lg={4}>
              <div
                role='radio'
                aria-checked={selected}
                aria-disabled={readOnly}
                aria-label={option.label}
                tabIndex={readOnly ? -1 : 0}
                className={classNames(
                  'p-4 h-100 d-flex zocks-border-1.0',
                  styles.option,
                  vertical ? 'flex-column zocks-gap-1.25' : 'align-items-center gap-3',
                  {
                    [styles.selected]: selected,
                    [styles.readOnly]: readOnly,
                    [styles.muted]: muted,
                  },
                )}
                onClick={() => handleSelect(option.type)}
                onKeyDown={(event) => handleKeyDown(event, option.type)}
              >
                <i
                  className={classNames('bi fs-2', {
                    'bi-record-circle zocks-text-primary': selected,
                    'bi-circle zocks-text-gray-600': !selected && !muted,
                    'bi-circle zocks-text-gray-400': muted,
                  })}
                  aria-hidden='true'
                />
                <div className='d-flex flex-column zocks-gap-0.25'>
                  {vertical ? (
                    <span className={classNames('heading-small', muted ? 'zocks-text-gray-500' : 'zocks-text-gray-900', styles.verticalTitle)}>
                      {option.label}
                    </span>
                  ) : (
                    <h6 className={classNames('mb-0', muted ? 'zocks-text-gray-500' : 'zocks-text-gray-900')}>{option.label}</h6>
                  )}
                  <span className={classNames('paragraph-small', muted ? 'zocks-text-gray-400' : 'zocks-text-gray-600')}>{option.description}</span>
                </div>
              </div>
            </Col>
          )
        })}
      </Row>
      {showHint ? (
        <div className='d-flex align-items-center gap-2 rounded-3 border zocks-border-color-gray-200 zocks-bg-color-gray-100 p-2 paragraph-extra-small zocks-text-gray-900'>
          <i className='bi bi-info-circle fs-6' aria-hidden='true' />
          Check your MCP documentation for the required authentication method.
        </div>
      ) : null}
    </div>
  )
}
